import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  MeetingSlotsAction,
  UpdateDemoMeetingStatus,
} from "../../Redux/Actions/OrganizationAction";
import Breadcrumb from "../DefaultComponent/BreadCrumb";
import {
  formatDate,
  calculateTotalPages,
  capitalizeFirstLetter,
} from "../../utils/Helper";
import {
  Box,
  TextField,
  MenuItem,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Stack,
  Pagination,
  Button
} from "@mui/material";
import { StyledToggleButtonGroup } from "../DefaultComponent/FilterHelper/Tooglebutton";
import ToggleButton from "@mui/material/ToggleButton";
import { toast } from "react-toastify";
import Spinner from "../DefaultComponent/Spinner";
import CreateMeetingSlotModal from "./CreateMeetingSlotModal";

const statusOptions = [
  { value: "pending", label: "Pending", color: "orange" },
  { value: "inactive", label: "Inactive", color: "red" },
  { value: "active", label: "Active", color: "green" },
  { value: "completed", label: "Completed", color: "blue" },
];

const MeetingSlot: React.FC = () => {
  const dispatch = useDispatch<any>();
  const slotData = useSelector((s: any) => s.MeetingSlotData);
  const updateStatus = useSelector((s: any) => s.UpdateDemoMeetingStatus);
  const [meetings, setMeetings] = useState<any[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const rowsPerPage = 12;
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [ordering, setOrdering] = useState<"newest" | "oldest">("newest");
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const buildParams = () => {
    let p = `?page=${page}&page_size=${rowsPerPage}&ordering=${ordering}`;
    if (statusFilter !== "all") {
      p += `&status=${statusFilter}`;
    }
    return p;
  };

  const fetchSlots = () => {
    setLoading(true);
    dispatch(MeetingSlotsAction(buildParams()));
  };

  useEffect(fetchSlots, [dispatch, page, statusFilter, ordering]);

  useEffect(() => {
    if (slotData?.data?.row) {
      setLoading(false);
      setMeetings(slotData.data.row);
      setCount(slotData.data.total_count);
    }
  }, [slotData]);

  useEffect(() => {
    if (updateStatus?.status && !updateStatus.loading) {
      fetchSlots();
    }
  }, [updateStatus]);

  const handleCopy = (link: string) => {
    navigator.clipboard
      .writeText(link)
      .then(() => toast.success("Meeting link copied!"))
      .catch(() => toast.error("Copy failed"));
  };

  const handleStatusChange = (id: string, newStatus: string) => {
    setMeetings((prev) =>
      prev.map((m) => (m.id === id ? { ...m, meeting_status: newStatus } : m))
    );
    dispatch(
      UpdateDemoMeetingStatus({ id, body: { meeting_status: newStatus } })
    );
  };

  return (
    <>
      <div className="page-wrapper card">
        <Breadcrumb
          pageTitle="Meetings"
          breadcrumbs={[
            {
              label: "Help & Support",
              path: "/support_requests/meetings",
              isActive: false,
            },
            {
              label: "Meetings",
              path: "/support_requests/meetings",
              isActive: true,
            },
          ]}
        />

        <Box
          sx={{
            m: 2,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <StyledToggleButtonGroup
            color="primary"
            value={statusFilter}
            exclusive
            onChange={(_: any, val: any) => {
              if (val !== null) {
                setStatusFilter(val);
                setPage(1);
              }
            }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="pending">Pending</ToggleButton>
            <ToggleButton value="active">Active</ToggleButton>
            <ToggleButton value="inactive">Inactive</ToggleButton>
          </StyledToggleButtonGroup>

          {/* Ordering */}
          <TextField
            select
            label="Order"
            value={ordering}
            onChange={(e) => {
              setOrdering(e.target.value as "newest" | "oldest");
              setPage(1);
            }}
            size="small"
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="newest">Newest</MenuItem>
            <MenuItem value="oldest">Oldest</MenuItem>
          </TextField>

          <Button
            variant="contained"
            onClick={() => setCreateModalOpen(true)}
            sx={{
              backgroundColor: "#0555c8",
              "&:hover": { backgroundColor: "#043c99" },
              ml: "auto",
            }}
          >
            Create Meeting Slot
          </Button>
        </Box>

        <CreateMeetingSlotModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSuccess={() => {
            setPage(1);
            fetchSlots();
          }}
        />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <Spinner />
          </Box>
        ) : (
          <Box sx={{ m: 2, overflowX: "auto" }}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ backgroundColor: "#0555c8" }}>
                  <TableRow>
                    <TableCell sx={{ color: "white" }}>S.No</TableCell>
                    <TableCell sx={{ color: "white" }}>Title</TableCell>
                    <TableCell sx={{ color: "white" }}>Start Time</TableCell>
                    <TableCell sx={{ color: "white" }}>End Time</TableCell>
                    <TableCell sx={{ color: "white" }}>Status</TableCell>
                    <TableCell sx={{ color: "white" }}>Link</TableCell>
                    <TableCell sx={{ color: "white" }}>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {meetings.map((m, i) => (
                    <TableRow key={m.id}>
                      <TableCell>{(page - 1) * rowsPerPage + i + 1}</TableCell>
                      <TableCell>{m.title}</TableCell>
                      <TableCell>{formatDate(m.start_datetime)}</TableCell>
                      <TableCell>{formatDate(m.end_datetime)}</TableCell>
                      <TableCell>
                        <TextField
                          select
                          value={m.meeting_status}
                          onChange={(e) =>
                            handleStatusChange(m.id, e.target.value)
                          }
                          size="small"
                        >
                          {statusOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              <span
                                style={{
                                  marginRight: 8,
                                  color: opt.color,
                                }}
                              >
                                <i className="fa-solid fa-circle fa-2xs" />
                              </span>
                              {capitalizeFirstLetter(opt.label)}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        {m.meeting_link}
                        <span
                          onClick={() => handleCopy(m.meeting_link)}
                          style={{
                            cursor: "pointer",
                            marginLeft: 8,
                            color: "#007bff",
                          }}
                          title="Copy Link"
                        >
                          {/* <FaCopy /> */}
                          Copy
                        </span>
                      </TableCell>
                      <TableCell>{m.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        <Box sx={{ m: 2, display: "flex", justifyContent: "center" }}>
          <Stack spacing={2}>
            <Pagination
              count={calculateTotalPages(count, rowsPerPage)}
              page={page}
              onChange={(_, np) => setPage(np)}
              color="primary"
              variant="outlined"
              shape="rounded"
            />
          </Stack>
        </Box>
      </div>
    </>
  );
};

export default MeetingSlot;
